import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Star } from "lucide-react";
import { MaskedLines } from "@/components/motion/Reveal";
import { IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yFrame = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yWave = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const scaleFrame = useTransform(scrollYProgress, [0, 1], [1, 1.04]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-32 pb-16"
      data-testid="hero-section"
    >
      {/* backdrop */}
      <div className="absolute inset-0 -z-10 grid-lines opacity-70" />
      <motion.img
        src={IMAGES.wave}
        alt=""
        style={{ y: yWave }}
        className="pointer-events-none absolute -top-10 right-0 w-[70%] max-w-4xl opacity-60 select-none"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/40 via-white/70 to-white" />

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Left copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-[#EEF1F4] bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#6B7280]"
              data-testid="hero-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#374151]" />
              Empowering Global Customer Engagement with AI
            </motion.div>

            <h1 className="mt-7 font-display font-extrabold tracking-tighter leading-[0.95] text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl">
              <MaskedLines
                delay={0.15}
                lines={["AI-Powered", "Customer Experience"]}
              />
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-[#9CA3AF]"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.34 }}
                >
                  for Modern Business
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
              className="mt-7 max-w-xl text-base lg:text-lg leading-relaxed text-[#6B7280]"
            >
              Deliver seamless customer engagement through an intelligent,
              cloud-based contact center and AI enterprise platform designed for
              global organizations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.62 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <motion.a
                href="#contact"
                data-testid="hero-get-started"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="group inline-flex items-center gap-2 rounded-full bg-[#374151] px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-gray-300/50"
              >
                Get Started
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.a>
              <motion.a
                href="#solutions"
                data-testid="hero-contact-sales"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full bg-[#F5F6F8] px-7 py-4 text-sm font-semibold text-[#1F2937] border border-[#EEF1F4]"
              >
                <Play className="h-3.5 w-3.5 fill-[#374151] text-[#374151]" />
                Contact Sales
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.85 }}
              className="mt-10 flex items-center gap-5"
            >
              <div className="flex -space-x-2">
                {["#374151", "#6B7280", "#9CA3AF", "#d1d5db"].map((c, i) => (
                  <span
                    key={i}
                    className="h-8 w-8 rounded-full border-2 border-white"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <div className="text-sm text-[#6B7280]">
                <span className="inline-flex items-center gap-1 text-[#1F2937] font-semibold">
                  <Star className="h-3.5 w-3.5 fill-[#374151] text-[#374151]" />
                  4.9/5
                </span>{" "}
                trusted by 3,000+ global teams
              </div>
            </motion.div>
          </div>

          {/* Right product frame */}
          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: yFrame, scale: scaleFrame }}
              initial={{ opacity: 0, y: 40, rotateX: 8 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.4 }}
              className="relative rounded-3xl border border-[#EEF1F4] bg-white p-2 shadow-2xl shadow-gray-300/50"
              data-testid="hero-product-frame"
            >
              <div className="overflow-hidden rounded-[18px]">
                <img
                  src={IMAGES.dashboard}
                  alt="SOBOT contact center dashboard"
                  className="w-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              style={{ y: yOrb }}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.7 }}
              className="absolute -left-8 -bottom-10 w-40 sm:w-48"
            >
              <motion.img
                src={IMAGES.orb}
                alt=""
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="w-full drop-shadow-xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="absolute -right-4 top-8 rounded-2xl border border-[#EEF1F4] bg-white/90 backdrop-blur px-4 py-3 shadow-xl shadow-gray-200/60"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">
                Resolution rate
              </p>
              <p className="font-display text-2xl font-bold text-[#1F2937]">
                +42%
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
